import React, { useEffect, useState, useRef } from 'react';
import { ElementData, CombinationResult } from '../types';
import { ELEMENTS, COMBINATIONS, HOLOLAB_COLORS } from '../constants';
import { getMascotFact } from '../utils/mascot';
import MascotAvatar from './MascotAvatar';
import { TrackingData } from '../types';
import clickSound from '../assets/sounds/click.wav';

interface DashboardProps {
  isOpen: boolean;
  onClose: () => void;
  savedElements: ElementData[];
  labSlots: ElementData[];
  onStartQuiz: (difficulty: 'easy' | 'medium') => void;
}

const TrashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-5 h-5"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 11v6M14 11v6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Dashboard: React.FC<DashboardProps> = ({ isOpen, onClose, savedElements, labSlots, onStartQuiz }) => {
  const [allDiscoverables, setAllDiscoverables] = useState<ElementData[]>([]);
  const [selectedInfo, setSelectedInfo] = useState<ElementData | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<ElementData[]>([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const MAX_SLOTS = 8;

  // Click sound helper
  const playClickSound = () => {
    try {
      const audio = new Audio(clickSound);
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignore errors if audio fails to play (e.g., user hasn't interacted yet)
      });
    } catch (error) {
      // Ignore errors
    }
  };

  // Reset progress function
  const handleResetProgress = () => {
    if (showResetConfirm) {
      // Clear all progress
      localStorage.removeItem('chemLabHistory');
      localStorage.removeItem('labCreatedSlots');

      // Reload the page to reset state
      window.location.reload();
    } else {
      // Show confirmation
      playClickSound();
      setShowResetConfirm(true);

      // Auto-hide confirmation after 5 seconds
      setTimeout(() => {
        setShowResetConfirm(false);
      }, 5000);
    }
  };

  // Tracking Ref for the Dashboard Avatar
  const dashboardTrackingRef = useRef<TrackingData>({
    left: { 
      pinchDistance: 0, 
      isPinching: false, 
      isPointing: false, 
      position: {x: 0.5, y: 0.5, z: 0}, 
      indexPosition: {x: 0.5, y: 0.5, z: 0},
      isDetected: false,
      isPresent: false
    },
    right: { 
      pinchDistance: 0, 
      isPinching: false, 
      isPointing: false, 
      position: {x: 0.5, y: 0.5, z: 0}, 
      indexPosition: {x: 0.5, y: 0.5, z: 0},
      isDetected: false,
      isPresent: false
    },
    isClapping: false,
    isResetGesture: false,
    isClosedFist: false,
    isSixtySevenGesture: false,
    handDistance: 1000,
    cameraAspect: 1.77
  });

  // Sync selectedSlots with labSlots prop (which updates when new elements are created)
  useEffect(() => {
    setSelectedSlots(labSlots);
  }, [labSlots]); // Update whenever labSlots changes (including when new elements are created in lab)

  // Save slots to localStorage whenever they change
  useEffect(() => {
    if (selectedSlots.length > 0) {
      localStorage.setItem('labSlots', JSON.stringify(selectedSlots));
    }
  }, [selectedSlots]);

  useEffect(() => {
    // 1. Base elements (Level 1) - assign level 1 explicitly
    const baseElements = ELEMENTS.map(el => ({ ...el, level: 1 }));
    
    // 2. Get all unique combination results (Level 2) - assign level 2 explicitly
    const comboResults = COMBINATIONS.map(c => ({ ...c.result, level: 2 }));
    // Deduplicate by symbol
    const uniqueCombos = comboResults.filter((v, i, a) => a.findIndex(t => t.symbol === v.symbol) === i);
    
    // 3. Combine base elements + compounds
    const fullList = [...baseElements, ...uniqueCombos];
    setAllDiscoverables(fullList);
  }, []);

  // Effect to track selected element position
  useEffect(() => {
    if (selectedInfo && isOpen) {
        // Small delay to allow render
        setTimeout(() => {
            const el = document.getElementById(`dashboard-item-${selectedInfo.symbol}`);
            if (el) {
                const rect = el.getBoundingClientRect();
                // Calculate normalized center (0-1) relative to window
                const centerX = (rect.left + rect.width / 2) / window.innerWidth;
                const centerY = (rect.top + rect.height / 2) / window.innerHeight;
                
                // Update ref to simulate a "hand" at the element's position
                // We use the 'left' hand slot for this target
                dashboardTrackingRef.current.left.position.x = centerX;
                dashboardTrackingRef.current.left.position.y = centerY;
                dashboardTrackingRef.current.left.isDetected = true;
                dashboardTrackingRef.current.left.isPresent = true;
            }
        }, 50);
    } else {
        // Go back to idle look
        dashboardTrackingRef.current.left.isDetected = false;
        dashboardTrackingRef.current.left.isPresent = false;
    }
  }, [selectedInfo, isOpen]);

  // Calculate progress
  const unlockedCount = allDiscoverables.filter(d => 
    savedElements.some(s => s.symbol === d.symbol) || ELEMENTS.some(e => e.symbol === d.symbol)
  ).length;
  
  const isUnlocked = (el: ElementData) => {
    // Base elements (Level 1, atomicNumber > 0) are always unlocked
    if (el.level === 1 && el.atomicNumber > 0) return true;
    // Compounds (Level 2+) need to be discovered/saved
    return savedElements.some(s => s.symbol === el.symbol);
  };

  // Group elements by Level and sort them
  const getElementsByLevel = (level: number) => {
      // Level 1: Only base elements (from ELEMENTS array, atomicNumber > 0)
      // Compounds (atomicNumber === 0) should NEVER appear in Level 1
      if (level === 1) {
          const baseElements = allDiscoverables.filter(el => 
              el.atomicNumber > 0 && (el.level === 1 || !el.level)
          );
          return baseElements.sort((a, b) => a.atomicNumber - b.atomicNumber);
      }
      
      // Level 2: Only compounds (from COMBINATIONS, atomicNumber === 0)
      // Base elements (atomicNumber > 0) should NEVER appear in Level 2
      if (level === 2) {
          const compounds = allDiscoverables.filter(el => 
              el.atomicNumber === 0 && (el.level === 2 || !el.level)
          );
          return compounds.sort((a, b) => a.symbol.localeCompare(b.symbol));
      }
      
      // Level 3+: Future complex compounds
      const filtered = allDiscoverables.filter(el => el.level === level);
      return filtered.sort((a, b) => a.symbol.localeCompare(b.symbol));
  };

  // Check if element is in selected slots
  const isInSlot = (el: ElementData) => {
    return selectedSlots.some(s => s.symbol === el.symbol);
  };

  // Toggle element in/out of slots
  const toggleSlot = (el: ElementData) => {
    if (isInSlot(el)) {
      // Remove from slots
      setSelectedSlots(prev => prev.filter(s => s.symbol !== el.symbol));
    } else {
      // Add to slots (if not at max)
      if (selectedSlots.length < MAX_SLOTS) {
        setSelectedSlots(prev => [...prev, el]);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="absolute inset-0 z-[100] flex flex-col items-center justify-center p-8 animate-fadeIn"
      style={{ backgroundColor: HOLOLAB_COLORS.deepBlue }}
    >
      
      {/* Header */}
      <div className="w-full max-w-6xl flex justify-between items-end mb-8 border-b pb-4" style={{ borderColor: HOLOLAB_COLORS.deepBlue10 }}>
        <div>
          <h1
            className="text-4xl md:text-6xl font-semibold"
            style={{ fontFamily: "'Inter', sans-serif", color: HOLOLAB_COLORS.labWhite }}
          >
            HOLOLAB
          </h1>
          <p
            className="text-sm tracking-widest mt-2"
            style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
          >
            RESEARCH & SYNTHESIS INTERFACE
          </p>
        </div>
        <div className="flex gap-4 items-end">
          {/* Reset Progress Button */}
          <button
            onClick={handleResetProgress}
            className="interactable-btn px-4 py-2 rounded-lg transition-all flex items-center gap-2 hover:opacity-80"
            style={{
              backgroundColor: showResetConfirm ? HOLOLAB_COLORS.errorRed : HOLOLAB_COLORS.labWhite,
              border: `1px solid ${showResetConfirm ? HOLOLAB_COLORS.errorRed : HOLOLAB_COLORS.deepBlue08}`,
              color: showResetConfirm ? HOLOLAB_COLORS.labWhite : HOLOLAB_COLORS.errorRed
            }}
          >
            <TrashIcon />
            <span
              className="text-xs font-semibold tracking-wide"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {showResetConfirm ? 'CLICK TO CONFIRM' : 'RESET PROGRESS'}
            </span>
          </button>

          {/* Discovery Progress */}
          <div className="text-right">
             <div
               className="text-xs uppercase mb-1"
               style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.labWhite60 }}
             >
               Discovery Progress
             </div>
             <div
               className="text-2xl font-bold"
               style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
             >
               {Math.floor((unlockedCount / allDiscoverables.length) * 100)}%
             </div>
             <div className="w-32 h-1 mt-1 rounded-full overflow-hidden" style={{ backgroundColor: HOLOLAB_COLORS.deepBlue10 }}>
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${(unlockedCount / allDiscoverables.length) * 100}%`,
                    backgroundColor: HOLOLAB_COLORS.precisionBlue
                  }}
                ></div>
             </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="flex-1 w-full max-w-6xl flex gap-8 overflow-hidden">
          
          {/* Element Grid - Grouped by Level */}
          <div className="flex-1 overflow-y-auto pr-4 no-scrollbar flex flex-col gap-8 pb-20">
             
             {/* LEVEL 1: BASE ELEMENTS */}
             <div>
                <h3
                  className="text-sm tracking-widest mb-4 border-b pb-2"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: HOLOLAB_COLORS.precisionBlue,
                    borderColor: HOLOLAB_COLORS.precisionBlue20
                  }}
                >
                  LEVEL 1 // BASE ELEMENTS
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                    {getElementsByLevel(1).map(el => (
                        <DashboardItem
                            key={el.symbol}
                            el={el}
                            unlocked={true}
                            inSlot={isInSlot(el)}
                            onClick={() => setSelectedInfo(el)}
                            onToggleSlot={() => toggleSlot(el)}
                            canAdd={selectedSlots.length < MAX_SLOTS}
                        />
                    ))}
                </div>
             </div>

             {/* LEVEL 2: COMPOUNDS */}
             <div>
                <h3
                  className="text-sm tracking-widest mb-4 border-b pb-2"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: HOLOLAB_COLORS.indigo,
                    borderColor: HOLOLAB_COLORS.indigo20
                  }}
                >
                  LEVEL 2 // COMPOUNDS
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                    {getElementsByLevel(2).map(el => (
                        <DashboardItem
                            key={el.symbol}
                            el={el}
                            unlocked={isUnlocked(el)}
                            inSlot={isInSlot(el)}
                            onClick={() => isUnlocked(el) && setSelectedInfo(el)}
                            onToggleSlot={() => isUnlocked(el) && toggleSlot(el)}
                            canAdd={selectedSlots.length < MAX_SLOTS}
                        />
                    ))}
                </div>
             </div>

             {/* LEVEL 3: COMING SOON */}
             <div>
                <h3
                  className="text-sm tracking-widest mb-4 border-b pb-2"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: HOLOLAB_COLORS.labWhite40,
                    borderColor: HOLOLAB_COLORS.labWhite10
                  }}
                >
                  LEVEL 3 // COMPLEX (LOCKED)
                </h3>
                 <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 opacity-90">
                    <div
                      className="aspect-square rounded-xl border flex flex-col items-center justify-center relative grayscale cursor-not-allowed"
                      style={{ borderColor: HOLOLAB_COLORS.labWhite10, backgroundColor: HOLOLAB_COLORS.labWhite5 }}
                    >
                        <div className="text-2xl" style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.labWhite40 }}>?</div>
                    </div>
                     <div
                       className="aspect-square rounded-xl border flex flex-col items-center justify-center relative grayscale cursor-not-allowed"
                       style={{ borderColor: HOLOLAB_COLORS.labWhite10, backgroundColor: HOLOLAB_COLORS.labWhite5 }}
                     >
                        <div className="text-2xl" style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.labWhite40 }}>?</div>
                    </div>
                </div>
             </div>

          </div>

          {/* Mascot / Info Panel */}
          <div className="w-80 flex-shrink-0 flex flex-col gap-4">
             
             {/* Quiz Section */}
             <div
               className="rounded-xl p-4 shadow-sm"
               style={{
                 backgroundColor: HOLOLAB_COLORS.labWhite,
                 border: `1px solid ${HOLOLAB_COLORS.deepBlue08}`
               }}
             >
                <div
                  className="text-xs uppercase tracking-widest mb-3"
                  style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.deepBlue }}
                >
                  Training Modules
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                          playClickSound();
                          onStartQuiz('easy');
                        }}
                        className="flex-1 rounded-lg py-2 px-3 transition-all interactable-btn hover:opacity-80"
                        style={{
                          backgroundColor: HOLOLAB_COLORS.iceBlue,
                          border: `1px solid ${HOLOLAB_COLORS.precisionBlue20}`
                        }}
                        id="dashboard-quiz-easy"
                    >
                        <div
                          className="text-[10px]"
                          style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
                        >
                          EASY
                        </div>
                        <div
                          className="text-sm font-semibold"
                          style={{ fontFamily: "'Inter', sans-serif", color: HOLOLAB_COLORS.deepBlue }}
                        >
                          Create Water
                        </div>
                    </button>
                    <button
                        onClick={() => {
                          playClickSound();
                          onStartQuiz('medium');
                        }}
                        className="flex-1 rounded-lg py-2 px-3 transition-all interactable-btn hover:opacity-80"
                        style={{
                          backgroundColor: HOLOLAB_COLORS.iceBlue,
                          border: `1px solid ${HOLOLAB_COLORS.precisionBlue20}`
                        }}
                         id="dashboard-quiz-medium"
                    >
                        <div
                          className="text-[10px]"
                          style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
                        >
                          MEDIUM
                        </div>
                        <div
                          className="text-sm font-semibold"
                          style={{ fontFamily: "'Inter', sans-serif", color: HOLOLAB_COLORS.deepBlue }}
                        >
                          Soda Water
                        </div>
                    </button>
                </div>
             </div>

             {/* Slot Management Panel */}
             <div
               className="rounded-xl p-4 shadow-sm"
               style={{
                 backgroundColor: HOLOLAB_COLORS.labWhite,
                 border: `1px solid ${HOLOLAB_COLORS.deepBlue08}`
               }}
             >
                <div className="flex items-center justify-between mb-3">
                    <div
                      className="text-xs uppercase tracking-widest"
                      style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.deepBlue }}
                    >
                      Lab Slots
                    </div>
                    <div
                      className="text-sm font-bold"
                      style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
                    >
                        {selectedSlots.length} / {MAX_SLOTS}
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                    {Array.from({ length: MAX_SLOTS }).map((_, i) => {
                        const slotElement = selectedSlots[i];
                        return (
                            <div
                                key={i}
                                className="aspect-square rounded-lg border-2 flex items-center justify-center text-xs"
                                style={slotElement
                                  ? {
                                      borderColor: HOLOLAB_COLORS.precisionBlue,
                                      backgroundColor: HOLOLAB_COLORS.iceBlue,
                                      color: slotElement.color
                                    }
                                  : {
                                      borderColor: HOLOLAB_COLORS.deepBlue10,
                                      backgroundColor: HOLOLAB_COLORS.iceBlue,
                                      borderStyle: 'dashed',
                                      color: HOLOLAB_COLORS.deepBlue40
                                    }
                                }
                            >
                                {slotElement ? slotElement.symbol : '+'}
                            </div>
                        );
                    })}
                </div>
                {selectedSlots.length === 0 && (
                    <div
                      className="text-xs mt-2 text-center"
                      style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.warningAmber }}
                    >
                        Select elements to add to lab slots
                    </div>
                )}
             </div>

             {/* Mascot Area */}
             <div
               className="rounded-xl p-6 relative overflow-hidden flex flex-col shadow-sm"
               style={{
                 backgroundColor: HOLOLAB_COLORS.labWhite,
                 border: `1px solid ${HOLOLAB_COLORS.deepBlue08}`
               }}
             >
                 {/* Background Element Symbol - Top Left, 50% space */}
                 {selectedInfo && (
                     <div
                         className="absolute -top-4 left-0 pointer-events-none"
                         style={{
                             width: '50%',
                             height: '50%',
                             fontSize: '8rem',
                             fontWeight: 'bold',
                             fontFamily: "'Inter', sans-serif",
                             color: selectedInfo.color,
                             opacity: 0.1,
                             zIndex: 0,
                             display: 'flex',
                             alignItems: 'flex-start',
                             justifyContent: 'flex-start',
                             padding: '1rem'
                         }}
                     >
                         {selectedInfo.symbol}
                     </div>
                 )}
                 {/* Mascot Graphic (Live 3D Render) */}
                 <div className="w-full h-48 relative -mt-4 mb-2" style={{ zIndex: 1 }}>
                    {/* Subtle glow - reduced from original */}
                    <div
                      className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 w-24 h-24 rounded-full blur-2xl"
                      style={{ backgroundColor: HOLOLAB_COLORS.precisionBlue20 }}
                    ></div>
                    {/* Avatar - Positioned bottom right */}
                    <div className="absolute bottom-0 -right-14" style={{ transform: 'translate(15%, 15%)' }}>
                        <MascotAvatar trackingData={dashboardTrackingRef} />
                    </div>
                 </div>

                 <div className="relative z-10 border-t pt-4" style={{ borderColor: HOLOLAB_COLORS.deepBlue10 }}>
                    <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{ backgroundColor: HOLOLAB_COLORS.precisionBlue }}
                        ></div>
                        <div
                          className="text-xs font-bold uppercase tracking-widest"
                          style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
                        >
                          Element Analysis
                        </div>
                    </div>
                    
                    <p
                      className="text-xs leading-relaxed min-h-[80px]"
                      style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.deepBlue }}
                    >
                        {selectedInfo
                          ? getMascotFact(selectedInfo)
                          : "Select an element from the grid to view its properties. I'll analyze the chemical structure for you!"}
                    </p>
                 </div>
             </div>

             {/* Close Button */}
             <button
                id="dashboard-close-btn"
                onClick={() => {
                  playClickSound();
                  onClose();
                }}
                disabled={selectedSlots.length === 0}
                className="interactable-btn mt-auto w-full py-4 font-semibold tracking-widest rounded-xl transition-all"
                style={selectedSlots.length === 0
                  ? {
                      backgroundColor: HOLOLAB_COLORS.deepBlue40,
                      color: HOLOLAB_COLORS.labWhite60,
                      cursor: 'not-allowed',
                      fontFamily: "'Inter', sans-serif"
                    }
                  : {
                      backgroundColor: HOLOLAB_COLORS.precisionBlue,
                      color: HOLOLAB_COLORS.labWhite,
                      fontFamily: "'Inter', sans-serif",
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }
                }
             >
                {selectedSlots.length === 0 ? 'SELECT SLOTS TO ENTER' : 'ENTER LABORATORY'}
             </button>
          </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

const DashboardItem = ({
    el,
    unlocked,
    inSlot,
    onClick,
    onToggleSlot,
    canAdd
}: {
    el: ElementData,
    unlocked: boolean,
    inSlot: boolean,
    onClick: () => void,
    onToggleSlot: () => void,
    canAdd: boolean
}) => {
  // Click sound helper
  const playClickSound = () => {
    try {
      const audio = new Audio(clickSound);
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignore errors if audio fails to play (e.g., user hasn't interacted yet)
      });
    } catch (error) {
      // Ignore errors
    }
  };

  const handleClick = () => {
    playClickSound();
    onClick();
  };

  const handleDoubleClick = () => {
    if (unlocked) {
      playClickSound();
      onToggleSlot();
    }
  };

  // Determine styles based on state
  const getItemStyles = () => {
    if (!unlocked) {
      return {
        borderColor: HOLOLAB_COLORS.labWhite10,
        backgroundColor: HOLOLAB_COLORS.deepBlue,
        opacity: 0.7
      };
    }
    if (inSlot) {
      return {
        borderColor: HOLOLAB_COLORS.precisionBlue,
        backgroundColor: HOLOLAB_COLORS.labWhite,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      };
    }
    return {
      borderColor: HOLOLAB_COLORS.deepBlue10,
      backgroundColor: HOLOLAB_COLORS.labWhite
    };
  };

  return (
    <div
    id={`dashboard-item-${el.symbol}`} // Hook for hit test
    className="interactable-btn aspect-square rounded-xl border-2 flex flex-col items-center justify-center relative cursor-pointer transition-all duration-200 hover:shadow-md"
    onClick={handleClick}
    onDoubleClick={handleDoubleClick}
    style={getItemStyles()}
    >
    {unlocked ? (
        <>
            {el.atomicNumber > 0 && (
                 <div
                   className="absolute top-2 left-2 text-base"
                   style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.deepBlue40 }}
                 >
                   {el.atomicNumber}
                 </div>
            )}
            {inSlot && (
                <div
                  className="absolute top-2 right-2 w-3 h-3 rounded-full border"
                  style={{
                    backgroundColor: HOLOLAB_COLORS.precisionBlue,
                    borderColor: HOLOLAB_COLORS.labWhite
                  }}
                ></div>
            )}
            {!inSlot && (
                <div
                  className="absolute top-2 right-2 w-2 h-2 rounded-full"
                  style={{ backgroundColor: HOLOLAB_COLORS.successGreen }}
                ></div>
            )}
            <div
              className="text-2xl md:text-3xl font-bold mb-1"
              style={{ fontFamily: "'Inter', sans-serif", color: el.color }}
            >
              {el.symbol}
            </div>
            <div
              className="text-[8px] md:text-[10px] uppercase tracking-wider text-center px-1"
              style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.deepBlue60 }}
            >
              {el.name}
            </div>
            {unlocked && (
                <div
                  className="absolute bottom-1 text-[8px]"
                  style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.precisionBlue }}
                >
                    {inSlot ? 'IN SLOT' : canAdd ? 'DBL-CLICK TO ADD' : 'SLOTS FULL'}
                </div>
            )}
        </>
    ) : (
            <div
              className="text-3xl"
              style={{ fontFamily: "'JetBrains Mono', monospace", color: HOLOLAB_COLORS.labWhite40 }}
            >
              ?
            </div>
    )}
    </div>
  );
};

export default Dashboard;
